import { Spacing } from '@persona-id/design-tokens';
import { ChevronDownIcon, ChevronUpIcon } from '@persona-id/design-tokens/icons';
import { isEmpty, uniq } from 'lodash/fp';
import styled from 'styled-components';

import {
  EXPIRATION_INTERVAL_LABELS,
  ONE_MINUTE_SECONDS,
} from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionExpirationsConfig/FormSectionExpirationsConfig';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { formatStepPropertyValues } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffSummary/InquiryTemplateVersionDiffSummary';
import {
  Column,
  CountryCodeMap,
  HashDiff,
  SetDiff,
  StepsArrayDiff,
  VersionDiff,
  VersionDiffChangeType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { changeTypeForColumn } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/utils';
import { uiStepAllComponentConfigs } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import Table, {
  Cell,
  PropertyRow,
  Row,
  TableRowWithDiffTag,
} from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable';
import {
  ExpirationIntervals,
  InquiryTemplateVersion,
  InquiryTemplateVersionStep,
  isCombinedStepConfig,
  isGovernmentIdStepConfig,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import {
  COUNTRIES,
  CountryCode,
  INQUIRY_LANGUAGES_ENGLISH,
  InquiryLanguage,
} from 'lib/constants/locales';
import useToggle from 'lib/hooks/useToggle';
import { isInputCountrySelectComponentConfig } from 'lib/interfaces/ui-components';

const StyledPropertyRow = styled(PropertyRow)`
  margin: ${Spacing[3]} 0;
`;

function EmbeddedVersionDiffTable<T>({
  title,
  changes,
  target,
  showUnchanged,
  formatFn,
}: {
  title: string;
  changes: SetDiff<T> | undefined;
  target: T[] | undefined;
  showUnchanged: boolean;
  formatFn: (value: T) => string;
}) {
  const [isOpen, { toggle }] = useToggle(true);
  const noChanges = isEmpty(changes?.added) && isEmpty(changes?.removed);
  if (noChanges && !showUnchanged) {
    return <></>;
  }

  return (
    <>
      <Row headerRow onClick={toggle}>
        <Cell>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isOpen ? <ChevronDownIcon /> : <ChevronUpIcon />}
            <Spacer width={0.5} height={0} />
            <Label weight='bold'>{title}</Label>
          </div>
        </Cell>
        <Cell></Cell>
      </Row>
      {isOpen && (
        <>
          {changes?.added?.map((changedValue: T, index) => (
            <TableRowWithDiffTag
              label={formatFn(changedValue)}
              changeType={VersionDiffChangeType.ADDED}
              key={index}
            />
          ))}
          {changes?.removed?.map((changedValue: T, index) => (
            <TableRowWithDiffTag
              label={formatFn(changedValue)}
              changeType={VersionDiffChangeType.REMOVED}
              key={index}
            />
          ))}
          {showUnchanged &&
            target?.map(
              (targetValue: T, index) =>
                !changes?.added?.includes(targetValue) &&
                !changes?.removed?.includes(targetValue) && (
                  <TableRowWithDiffTag
                    label={formatFn(targetValue)}
                    changeType={undefined}
                    key={index}
                  />
                ),
            )}
        </>
      )}
    </>
  );
}

const secondsToMinutes = (seconds: number) => {
  return Math.floor(seconds / ONE_MINUTE_SECONDS);
};

const EmbeddedExpiredIntervalsTable = ({
  showUnchanged,
  changeColumn,
  expirationIntervalChanges,
  expirationIntervals,
}: {
  showUnchanged: boolean;
  changeColumn: Column;
  expirationIntervalChanges: HashDiff<keyof ExpirationIntervals>;
  expirationIntervals: ExpirationIntervals;
}) => {
  return (
    <>
      {Object.entries(expirationIntervalChanges).map(([expirationIntervalKey, change], index) => {
        const intervalValue = changeColumn === Column.TARGET ? change.value : change['prev-value'];
        const changeType = changeTypeForColumn(changeColumn, change['change-type']);
        return (
          <StyledPropertyRow
            key={index}
            textKey={EXPIRATION_INTERVAL_LABELS[expirationIntervalKey as keyof ExpirationIntervals]}
            value={intervalValue ? `${secondsToMinutes(intervalValue)} min` : '--'}
            changeType={changeType}
          />
        );
      })}
      {showUnchanged &&
        Object.entries(expirationIntervals)
          .filter(([key]) => !(key in expirationIntervalChanges))
          .map(([expirationIntervalKey, intervalValue]) => {
            return (
              <StyledPropertyRow
                key={expirationIntervalKey}
                textKey={
                  EXPIRATION_INTERVAL_LABELS[expirationIntervalKey as keyof ExpirationIntervals]
                }
                value={intervalValue ? `${secondsToMinutes(intervalValue)} min` : '--'}
              />
            );
          })}
    </>
  );
};

const ExpiredIntervalsTable = ({
  showUnchanged,
  expirationIntervalChanges,
  expirationIntervals,
}: {
  showUnchanged: boolean;
  expirationIntervalChanges: HashDiff<keyof ExpirationIntervals>;
  expirationIntervals: ExpirationIntervals;
}) => {
  if (isEmpty(expirationIntervalChanges) && !showUnchanged) {
    return <></>;
  }

  return (
    <Row data-test='version-diff-general__expired-interval-container'>
      <Cell>
        <EmbeddedExpiredIntervalsTable
          showUnchanged={showUnchanged}
          changeColumn={Column.BASE}
          expirationIntervalChanges={expirationIntervalChanges}
          expirationIntervals={expirationIntervals}
        />
      </Cell>
      <Cell>
        <EmbeddedExpiredIntervalsTable
          showUnchanged={showUnchanged}
          changeColumn={Column.TARGET}
          expirationIntervalChanges={expirationIntervalChanges}
          expirationIntervals={expirationIntervals}
        />
      </Cell>
    </Row>
  );
};

const SupportedLanguagesTable = ({
  enabledLocaleChanges,
  enabledLocales,
  showUnchanged,
}: {
  enabledLocaleChanges: SetDiff<InquiryLanguage> | undefined;
  enabledLocales: InquiryLanguage[];
  showUnchanged: boolean;
}) => {
  return (
    <EmbeddedVersionDiffTable<InquiryLanguage>
      title='Supported Languages'
      changes={enabledLocaleChanges}
      target={enabledLocales}
      showUnchanged={showUnchanged}
      formatFn={(language: InquiryLanguage) => INQUIRY_LANGUAGES_ENGLISH[language]}
    />
  );
};

// walks through input country select components and builds a map of component
// name to country codes that are enabled
// return Map<string, CountryCode[]>
// eg. { 'step-name:input-country-select': ['US', 'CA'] }
const buildCountrySelectMapFromSteps = (steps: InquiryTemplateVersionStep[]) => {
  const countrySelectMap: Record<string, CountryCode[]> = {};
  steps.filter(isUiStepConfig).forEach((step) => {
    uiStepAllComponentConfigs(step)
      .filter(isInputCountrySelectComponentConfig)
      .forEach((countrySelectConfig) => {
        countrySelectMap[[step.name, countrySelectConfig.name].join(':')] =
          countrySelectConfig.config['country-codes'];
      });
  });
  return countrySelectMap;
};

const CountrySelectTable = ({
  countrySelect,
  countrySelectChanges,
  showUnchanged,
  stepsMap,
}: {
  countrySelect: Record<string, CountryCode[]>;
  countrySelectChanges: CountryCodeMap;
  showUnchanged: boolean;
  stepsMap: Map<string, InquiryTemplateVersionStep>;
}) => {
  return (
    <>
      {uniq([...Object.keys(countrySelectChanges), ...Object.keys(countrySelect)]).map(
        (countrySelectId) => {
          // We need to perform the diffing logic using the step name since we require uniquness
          // but when we render the table we want to display the step display name
          const [stepName, componentName] = countrySelectId.split(':');
          const stepDisplayName = getDisplayNameForStep(stepsMap.get(stepName));
          return (
            <EmbeddedVersionDiffTable<CountryCode>
              key={countrySelectId}
              title={`Country Select Options: ${stepDisplayName}, ${componentName}`}
              changes={countrySelectChanges[countrySelectId]}
              target={countrySelect[countrySelectId]}
              showUnchanged={showUnchanged}
              formatFn={(countryCode: CountryCode) => COUNTRIES[countryCode]}
            />
          );
        },
      )}
    </>
  );
};

// walks through stepsDiff and extracts steps that have changes to capture methods
// eg. { 'verification-government-id': { added: ['desktop:upload'], removed: ['mobile:upload'] }
const buildCaptureMethodsChanges = (stepsChanges: StepsArrayDiff) => {
  const captureMethodChanges: Record<string, SetDiff<string>> = {};
  Object.entries(stepsChanges).forEach(([stepName, stepChanges]) => {
    const captureMethods = stepChanges.changes['capture-methods'];
    if (!isEmpty(captureMethods?.added) || !isEmpty(captureMethods?.removed)) {
      captureMethodChanges[stepName] = captureMethods;
    }
  });
  return captureMethodChanges;
};

// walks through steps and builds a map of step name to capture methods that are enabled
// e.g { 'verification-government-id': ['desktop:upload', 'mobile:upload', 'native-mobile:upload'] }
const buildCaptureMethodsMapFromSteps = (steps: InquiryTemplateVersionStep[]) => {
  const captureMethodsMap: Record<string, string[]> = {};
  steps.forEach((step) => {
    if (isCombinedStepConfig(step)) {
      const stepName = step.name;
      captureMethodsMap[stepName] = [];
      captureMethodsMap[stepName].push(
        ...step.config['enabled-capture-options-desktop'].map(
          (captureMethod: string) => 'desktop:' + captureMethod,
        ),
      );
      captureMethodsMap[stepName].push(
        ...step.config['enabled-capture-options-mobile'].map(
          (captureMethod: string) => 'mobile:' + captureMethod,
        ),
      );
      if (isGovernmentIdStepConfig(step)) {
        captureMethodsMap[stepName].push(
          ...step.config['enabled-capture-options-native-mobile'].map(
            (captureMethod: string) => 'native-mobile:' + captureMethod,
          ),
        );
      }
    }
  });
  return captureMethodsMap;
};

const CaptureMethodsTable = ({
  captureMethods,
  captureMethodChanges,
  showUnchanged,
  stepsMap,
}: {
  captureMethods: Record<string, string[]>;
  captureMethodChanges: Record<string, SetDiff<string>>;
  showUnchanged: boolean;
  stepsMap: Map<string, InquiryTemplateVersionStep>;
}) => {
  return (
    <>
      {uniq([...Object.keys(captureMethods), ...Object.keys(captureMethodChanges)]).map(
        (stepName) => {
          const stepDisplayName = getDisplayNameForStep(stepsMap.get(stepName));
          return (
            <EmbeddedVersionDiffTable<string>
              key={stepName}
              title={`Enabled Capture Methods: ${stepDisplayName}`}
              changes={captureMethodChanges[stepName]}
              target={captureMethods[stepName]}
              showUnchanged={showUnchanged}
              formatFn={formatStepPropertyValues}
            />
          );
        },
      )}
    </>
  );
};

// walks through stepsDiff and extracts step changes that have changes to device handoff options
// eg. { 'verification-government-id': { added: ['email'], removed: ['sms'] }
const buildDeviceHandoffOptionChanges = (stepsChanges: StepsArrayDiff) => {
  const deviceHandoffOptionChanges: Record<string, SetDiff<string>> = {};
  Object.entries(stepsChanges).forEach(([stepName, stepChanges]) => {
    const deviceHandoffOptions = stepChanges.changes['device-handoff-options'];
    if (!isEmpty(deviceHandoffOptions?.added) || !isEmpty(deviceHandoffOptions?.removed)) {
      deviceHandoffOptionChanges[stepName] = deviceHandoffOptions;
    }
  });
  return deviceHandoffOptionChanges;
};

// walks through steps and builds a map of step name to device handoff options that are enabled
// e.g { 'verification-government-id': ['email', 'sms'] }
const buildDeviceHandoffOptionMapFromSteps = (steps: InquiryTemplateVersionStep[]) => {
  const deviceHandoffOptionsMap: Record<string, string[]> = {};
  steps.forEach((step) => {
    if (isCombinedStepConfig(step)) {
      const stepName = step.name;
      deviceHandoffOptionsMap[stepName] = step.config['device-handoff-options'];
    }
  });
  return deviceHandoffOptionsMap;
};

const DeviceHandoffOptionsTable = ({
  deviceHandoffOptions,
  deviceHandoffOptionChanges,
  showUnchanged,
  stepsMap,
}: {
  deviceHandoffOptionChanges: Record<string, SetDiff<string>>;
  deviceHandoffOptions: Record<string, string[]>;
  showUnchanged: boolean;
  stepsMap: Map<string, InquiryTemplateVersionStep>;
}) => {
  return (
    <>
      {uniq([...Object.keys(deviceHandoffOptionChanges), ...Object.keys(deviceHandoffOptions)]).map(
        (stepName) => {
          const stepDisplayName = getDisplayNameForStep(stepsMap.get(stepName));
          return (
            <EmbeddedVersionDiffTable<string>
              key={stepName}
              title={`Device Handoff Options: ${stepDisplayName}`}
              changes={deviceHandoffOptionChanges[stepName]}
              target={deviceHandoffOptions[stepName]}
              showUnchanged={showUnchanged}
              formatFn={formatStepPropertyValues}
            />
          );
        },
      )}
    </>
  );
};

const GeneralTable = ({
  versionDiff,
  inquiryTemplateVersion,
  showUnchanged,
}: {
  versionDiff: VersionDiff;
  inquiryTemplateVersion: InquiryTemplateVersion;
  showUnchanged: boolean;
}) => {
  const {
    'expiration-intervals': expirationIntervalsChanges,
    'enabled-locales': enabledLocaleChanges,
    'country-codes': countrySelectChanges,
    steps: stepsChanges,
  } = versionDiff.inquiryTemplateVersionDiff;

  // capture methods and device handoff options changes are nested in steps changes
  const captureMethodChanges = buildCaptureMethodsChanges(stepsChanges);
  const deviceHandOffOptionChanges = buildDeviceHandoffOptionChanges(stepsChanges);

  const noChanges =
    [
      expirationIntervalsChanges,
      countrySelectChanges,
      captureMethodChanges,
      deviceHandOffOptionChanges,
    ].every((change) => isEmpty(change)) &&
    isEmpty(enabledLocaleChanges?.added) &&
    isEmpty(enabledLocaleChanges?.removed);
  if (noChanges && !showUnchanged) {
    return (
      <Label color='secondary' size='lg'>
        No changes made
      </Label>
    );
  }

  const inquiryTemplateVersionStepMap = new Map(
    inquiryTemplateVersion.attributes.steps.map((step) => [step.name, step]),
  );

  return (
    <Table>
      <Row>
        <Cell>
          <Label>Previous Version</Label>
        </Cell>
        <Cell>
          <Label>Current Version</Label>
        </Cell>
      </Row>
      <ExpiredIntervalsTable
        showUnchanged={showUnchanged}
        expirationIntervalChanges={expirationIntervalsChanges}
        expirationIntervals={inquiryTemplateVersion.attributes['expiration-intervals']}
      />
      <SupportedLanguagesTable
        enabledLocaleChanges={enabledLocaleChanges}
        enabledLocales={inquiryTemplateVersion.attributes['enabled-locales']}
        showUnchanged={showUnchanged}
      />
      <CountrySelectTable
        countrySelect={buildCountrySelectMapFromSteps(inquiryTemplateVersion.attributes.steps)}
        countrySelectChanges={countrySelectChanges}
        showUnchanged={showUnchanged}
        stepsMap={inquiryTemplateVersionStepMap}
      />
      <CaptureMethodsTable
        captureMethodChanges={captureMethodChanges}
        captureMethods={buildCaptureMethodsMapFromSteps(inquiryTemplateVersion.attributes.steps)}
        showUnchanged={showUnchanged}
        stepsMap={inquiryTemplateVersionStepMap}
      />
      <DeviceHandoffOptionsTable
        deviceHandoffOptionChanges={deviceHandOffOptionChanges}
        deviceHandoffOptions={buildDeviceHandoffOptionMapFromSteps(
          inquiryTemplateVersion.attributes.steps,
        )}
        stepsMap={inquiryTemplateVersionStepMap}
        showUnchanged={showUnchanged}
      />
    </Table>
  );
};

interface InquiryTemplateVersionDiffGeneralProps {
  versionDiff: VersionDiff;
  inquiryTemplateVersion: InquiryTemplateVersion;
}

const InquiryTemplateVersionDiffGeneral = ({
  versionDiff,
  inquiryTemplateVersion,
}: InquiryTemplateVersionDiffGeneralProps) => {
  const [showUnchanged, { toggle: toggleShowUnchanged }] = useToggle(false);
  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={[
            <ToggleSlider
              key='show-unchanged-toggle'
              titleOn='displaying all values'
              titleOff='only showing changes'
              labelOn='Show unchanged'
              labelOff='Show unchanged'
              value={showUnchanged}
              onChange={toggleShowUnchanged}
            />,
          ]}
        >
          General
        </Card.Title>
        <GeneralTable
          versionDiff={versionDiff}
          inquiryTemplateVersion={inquiryTemplateVersion}
          showUnchanged={showUnchanged}
        />
      </Card.Body>
    </Card>
  );
};

export default InquiryTemplateVersionDiffGeneral;
